import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import KeywordManager from './KeywordManager';
import ContentLinkPanel from './ContentLinkPanel';
import TokenCostModal from '../TokenCostModal';
import type { TaxonomyNode } from '../../pages/settings/TaxonomySettingsPage';

const EMBEDDING_GENERATION_TOKEN_COST = 100;

interface TaxonomyNodeCardProps {
  node: TaxonomyNode | null;
  onEdit?: (node: TaxonomyNode) => void;
  onDelete?: (node: TaxonomyNode) => void;
  onKeywordsUpdated?: () => void;
  onContentLinked?: () => void;
  onGenerateEmbedding?: (node: TaxonomyNode) => void;
}

export default function TaxonomyNodeCard({
  node,
  onEdit,
  onDelete,
  onKeywordsUpdated,
  onContentLinked,
  onGenerateEmbedding,
}: TaxonomyNodeCardProps) {
  const { t } = useTranslation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmbeddingModal, setShowEmbeddingModal] = useState(false);

  if (!node) {
    return (
      <div className="text-center py-8 text-gray-600 dark:text-gray-400">
        <p className="mb-2 font-medium">{t('settings.taxonomy.selectNode', 'Select a node')}</p>
        <p className="text-sm">
          {t('settings.taxonomy.selectNodeDescription', 'Click on a category in the tree to view and manage its details')}
        </p>
      </div>
    );
  }

  // Use breadcrumb names from backend (parent names + current node name)
  // Backend builds this from the path by looking up actual node names
  const breadcrumb = node.breadcrumbNames || [node.name];

  const handleDelete = () => {
    setShowDeleteModal(false);
    onDelete?.(node);
  };

  return (
    <div className="space-y-6">
      {/* Node Header */}
      <div>
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2 flex-wrap">
          {breadcrumb.map((part, index) => (
            <span key={index} className="flex items-center gap-2">
              {index > 0 && <span>/</span>}
              <span className={index === breadcrumb.length - 1 ? 'font-medium text-gray-900 dark:text-white' : ''}>
                {part}
              </span>
            </span>
          ))}
        </div>

        {/* Icon and Name */}
        <div className="flex items-start gap-3 mb-2">
          {node.icon && (
            <span className="text-4xl flex-shrink-0" aria-hidden="true">
              {node.icon}
            </span>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold dark:text-white break-words">{node.name}</h2>
            {node.color && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t('settings.taxonomy.color', 'Color')}:
                </span>
                <span
                  className="w-6 h-6 rounded border-2 border-gray-300 dark:border-gray-600"
                  style={{ backgroundColor: node.color }}
                  aria-label={node.color}
                />
                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{node.color}</span>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {node.description && (
          <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
            {node.description}
          </p>
        )}

        {/* Metadata */}
        <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <span className="font-medium">{t('settings.taxonomy.level', 'Level')}:</span>
            <span>{node.level}</span>
          </div>
          {node.linkedContentCount !== undefined && (
            <div className="flex items-center gap-1">
              <span className="font-medium">{t('settings.taxonomy.linkedContent', 'Linked Content')}:</span>
              <span>{node.linkedContentCount}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <span className="font-medium">{t('common.created', 'Created')}:</span>
            <span>{new Date(node.createdAt).toLocaleDateString('nl-NL')}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button variant="secondary" onClick={() => onEdit?.(node)}>
          {t('common.edit', 'Edit')}
        </Button>
        <Button variant="secondary" onClick={() => setShowDeleteModal(true)}>
          {t('common.delete', 'Delete')}
        </Button>
      </div>

      {/* Specific Items Section */}
      {node.specificItems && node.specificItems.length > 0 && (
        <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-4">
          <h3 className="text-lg font-bold dark:text-white mb-3">
            {t('settings.taxonomy.specificItems', 'Specific Items')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            {t('settings.taxonomy.specificItemsDescription', 'Concrete examples in this category')}
          </p>
          <div className="flex flex-wrap gap-2">
            {node.specificItems.map((item, index) => (
              <span
                key={index}
                className="inline-block px-3 py-1 text-sm bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-2 border-blue-200 dark:border-blue-800 rounded"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Keywords Section */}
      <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-4">
        <KeywordManager
          nodeId={node.id}
          nodeName={node.name}
          keywords={node.keywords || []}
          onKeywordsUpdated={() => onKeywordsUpdated?.()}
        />
      </div>

      {/* Content Links Section */}
      <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-4">
        <ContentLinkPanel
          nodeId={node.id}
          nodeName={node.name}
          linkedContentCount={node.linkedContentCount}
          onContentLinked={() => onContentLinked?.()}
        />
      </div>

      {/* Advanced Actions */}
      <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-4">
        <h3 className="text-lg font-bold dark:text-white mb-4">
          {t('settings.taxonomy.advancedActions', 'Advanced')}
        </h3>
        <Button
          variant="secondary"
          onClick={() => setShowEmbeddingModal(true)}
        >
          {t('settings.taxonomy.generateEmbedding', 'Regenerate Embedding')}
        </Button>
      </div>

      {/* Embedding Generation Modal */}
      <TokenCostModal
        open={showEmbeddingModal}
        onClose={() => setShowEmbeddingModal(false)}
        onConfirm={async () => {
          setShowEmbeddingModal(false);
          onGenerateEmbedding?.(node);
        }}
        title={t('tokenCostModal.embeddingGeneration.title', 'Generate Embedding')}
        description={t(
          'tokenCostModal.embeddingGeneration.description',
          'Generate a vector embedding for "{{nodeName}}" to enable semantic search and content matching.',
          { nodeName: node.name }
        )}
        tokenCost={EMBEDDING_GENERATION_TOKEN_COST}
        confirmButtonText={t('tokenCostModal.embeddingGeneration.confirm', 'Generate Embedding')}
        estimatedDuration={t('tokenCostModal.embeddingGeneration.duration', '5-10 seconds')}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 border-2 border-black dark:border-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-3 dark:text-white">
              {t('settings.taxonomy.deleteConfirmTitle', 'Delete Category?')}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              {t(
                'settings.taxonomy.deleteConfirmMessage',
                'Are you sure you want to delete "{{name}}"? This will also delete all child categories and unlink content.',
                { name: node.name }
              )}
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleDelete}>
                {t('common.delete', 'Delete')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
